// Pure helpers for « Employés » (CreateAgent.js) and its « Productivité » view
// (EmployeeProductivity.js). Payloads are the shapes the backend reads
// (Backend app/api/employees.py create-agent / UpdateEmployeeModel); counts
// come from /api/employees/roster-stats and /api/statistics/employee/* —
// nothing here recounts tickets.

// POST /api/employees/create-agent: name, email, description, and the
// external admin id only when one is typed.
export const buildCreatePayload = ({ name, email, description, externalAdminId }) => {
  const payload = { name, email, description };
  if ((externalAdminId || '').trim()) payload.external_admin_id = externalAdminId.trim();
  return payload;
};

// PUT /api/employees/update-employee: the current e-mail identifies the
// member, then only the fields that changed (new_email for a new address).
export const buildUpdatePayload = (current, form) => {
  const payload = { email: current.email };
  if (form.name !== current.name) payload.name = form.name;
  if (form.email !== current.email) payload.new_email = form.email;
  if (form.description !== current.description) payload.description = form.description;
  if (form.externalAdminId !== (current.external_admin_id || '')) payload.external_admin_id = form.externalAdminId.trim();
  return payload;
};

// « À vérifier »: members whose open tickets have had no activity for
// stale_after_days (roster-stats stale_open), most stale first. (The « members
// without an external id » row is gone: only an account whose ticketing tool
// reads that id needs it — Kevin, 29/10.)
export const teamAttention = (roster) => (roster?.employees || [])
  .filter((row) => (row.stale_open || 0) > 0)
  .sort((a, b) => b.stale_open - a.stale_open)
  .map((row) => ({ kind: 'stale', key: `stale-${row.employee_id}`, employeeId: row.employee_id, name: row.name, count: row.stale_open, open: row.open_assigned }));

const hasExternalId = (agent) => Boolean(String(agent?.external_admin_id || '').trim());

// The member's « identifiant dans votre outil de tickets » (external_admin_id)
// is read by one flow only: the external ticketing adapter that re-assigns a
// ticket in the tool it came from (Backend ticket_service.assign… →
// logistics_service.set_ticket_admin_id_external). No endpoint says whether an
// account's integration reads it (GET /api/integrations/config has no entry
// for accounts on the built-in adapter defaults), so the signal is the data:
// the account already uses it when a member carries one. The member being
// edited always shows a value it holds, so no stored id is ever hidden.
export const showsExternalId = (agents, editedAgent) => (agents || []).some(hasExternalId)
  || hasExternalId(editedAgent);

// « Ma productivité » (role employee): the tiles come from
// GET /api/statistics/me/productivity only — insights and performance stay
// admin endpoints. Shares are derived from the counts of the same response.
export const selfProductivityTiles = (productivity) => {
  if (!productivity) return null;
  const sent = productivity.total_sent_messages || 0;
  const unmodified = productivity.ai_validated_unmodified || 0;
  const aiTotal = unmodified + (productivity.ai_validated_modified || 0);
  return {
    sent,
    aiTotal,
    aiShare: sent > 0 ? Math.round((aiTotal / sent) * 100) : null,
    unmodified,
    unmodifiedShare: aiTotal > 0 ? Math.round((unmodified / aiTotal) * 100) : null,
    ticketsVisited: productivity.tickets_visited || 0,
    visits: productivity.visits_count || 0,
    timeOnTickets: productivity.total_time_on_tickets_seconds || 0,
    avgVisit: productivity.avg_time_on_ticket_seconds || 0,
  };
};

// Relative change vs the previous window, null when it cannot be read.
export const deltaPercent = (current, previous) => {
  if (current == null || previous == null || previous <= 0) return null;
  const pct = ((current - previous) / previous) * 100;
  if (!Number.isFinite(pct) || Math.abs(pct) < 1) return null;
  return Math.round(pct);
};

// Durations across 4 orders of magnitude (45 s … 12 j).
export const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  if (s < 90) return `${Math.round(s)} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  if (s < 172800) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return m > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.round((s % 86400) / 3600);
  return h > 0 ? `${d} j ${h} h` : `${d} j`;
};

export const formatHoursMinutes = (seconds) => {
  if (!seconds || Number.isNaN(Number(seconds))) return '0 min';
  const total = Math.round(Number(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
};

export const formatNumber = (value) => (value == null || Number.isNaN(Number(value)) ? '0' : Number(value).toLocaleString('fr-FR'));

export const formatDays = (days) => (days == null ? '—' : `${Number(days).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} j`);

// Sent messages per day: the API lists days with messages only; the window
// [start, end] is filled with zeros so a quiet day shows as an empty slot.
export const fillDailyCounts = (rows, start, end) => {
  const list = Array.isArray(rows) ? rows : [];
  const byDay = new Map(list.map((row) => [String(row.date).slice(0, 10), row.count || 0]));
  const parse = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
  };
  // A row outside the window (clock skew, UTC day) widens it: no count is ever dropped.
  const rowDays = list.map((row) => parse(row.date)).filter((ms) => ms != null);
  const first = Math.min(...[parse(start), ...rowDays].filter((ms) => ms != null));
  const last = Math.max(...[parse(end), ...rowDays].filter((ms) => ms != null));
  if (!Number.isFinite(first) || !Number.isFinite(last) || last < first || (last - first) / 86400000 > 400) {
    return list.map((row) => ({ date: String(row.date).slice(0, 10), count: row.count || 0 }));
  }
  const out = [];
  for (let ms = first; ms <= last; ms += 86400000) {
    const key = new Date(ms).toISOString().slice(0, 10);
    out.push({ date: key, count: byDay.get(key) || 0 });
  }
  return out;
};

// Score tone with the API's sentiment buckets (≤ 30 négatif, ≤ 60 neutre).
export const moodTone = (score) => (score <= 30 ? 'danger' : score <= 60 ? 'warning' : 'success');
